// components/dashboard/Experience.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import { getItem } from "../../services/Content";
import { useExperiences } from "../../context/ExperiencesContext";
import { useAuth } from "../../context/AuthContext";
import { AppButton } from "../ui";
import EditExperienceDialog from "../experiences/EditExperienceDialog";
import JoinCodeDialog from "../JoinCodeDialog";

const clamp2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const Experience = ({ experience }) => {
  const [systemLabel, setSystemLabel] = useState("");
  const [copied, setCopied] = useState({});
  const [snackOpen, setSnackOpen] = useState(false);
  const [joinCodeOpen, setJoinCodeOpen] = useState(false);

  const { guides } = useExperiences();
  const { hasClassroomAccess } = useAuth();

  const [editOpen, setEditOpen] = useState(false);

  const previewPath = `/viewer/${encodeURIComponent(experience.experienceId)}`;

  useEffect(() => {
    if (!experience.systemId) {
      setSystemLabel("Unknown system");
      return;
    }

    getItem("systems", experience.systemId, false)
      .then((response) => setSystemLabel(response?.name || "Unknown system"))
      .catch((err) => console.error("Error fetching system label:", err));
  }, [experience.systemId]);

  const guideLabel = guides?.find((g) => g.sk?.includes(experience.guideId))?.name || "No guide";

  const copyValue = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((p) => ({ ...p, [key]: true }));
      setSnackOpen(true);
      setTimeout(() => setCopied((p) => ({ ...p, [key]: false })), 2000);
    } catch (e) {
      console.error("Failed to copy:", e);
    }
  };

  return (
    <>
      <Card
        elevation={2}
        sx={{
          borderRadius: 1,
          p: 2.25,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 1.25, md: 2 },
          width: "100%",
          "&:hover": { boxShadow: 4 },
        }}
      >
        {/* Experience */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>
            Experience
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={clamp2}>
            {experience.name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
            <Chip size="small" variant="outlined" label="ID" />
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              {experience.experienceId}
            </Typography>
            <Tooltip title={copied.expId ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={() => copyValue("expId", experience.experienceId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }} />

        {/* System */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>
            System
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={clamp2}>
            {systemLabel}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
            <Chip size="small" color="primary" variant="outlined" label="System ID" />
            <Typography variant="caption" color="text.secondary">
              {experience.systemId}
            </Typography>
            <Tooltip title={copied.sysId ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={() => copyValue("sysId", experience.systemId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }} />

        {/* Guide */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>
            Guide
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={clamp2}>
            {guideLabel}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
            <Chip size="small" color="secondary" variant="outlined" label="Guide ID" />
            <Typography variant="caption" color="text.secondary">
              {experience.guideId}
            </Typography>
            <Tooltip title={copied.guideId ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={() => copyValue("guideId", experience.guideId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ ml: { md: "auto" }, mt: { xs: 0.5, md: 0 } }}
          flexShrink={0}
        >
          <AppButton
            kind="primary"
            size="small"
            startIcon={<OpenInNewIcon fontSize="small" />}
            href={previewPath}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview
          </AppButton>
          <AppButton
            kind="secondary"
            size="small"
            onClick={() => setJoinCodeOpen(true)}
          >
            {hasClassroomAccess ? "Join Code" : "Upgrade for Join Codes"}
          </AppButton>
          <AppButton
            kind="ghost"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => setEditOpen(true)}
          >
            Edit
          </AppButton>
        </Stack>
      </Card>

      <EditExperienceDialog
        open={editOpen}
        experience={experience}
        onClose={() => setEditOpen(false)}
      />

      <JoinCodeDialog
        open={joinCodeOpen}
        onClose={() => setJoinCodeOpen(false)}
        experienceId={experience.experienceId}
        experienceName={experience.name}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={1800}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSnackOpen(false)}>
          Copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};

export default Experience;
